import React, { useMemo, useState } from 'react';
import { Form } from '../api/client';
import { SortableTableHeader } from './ui';
import Sparkline from './Sparkline';
import { BanIcon, FlagIcon, TrashIcon } from './icons';
import './FormList.css';

interface FormListProps {
  forms: Form[];
  onSelectForm: (form: Form) => void;
  onDeleteForm?: (id: number) => void;
  selectedId?: number;
}

const FormList: React.FC<FormListProps> = ({
  forms,
  onSelectForm,
  onDeleteForm,
  selectedId,
}) => {
  const [sort, setSort] = useState<{ key: 'name' | 'domain' | 'total' | 'approved' | 'spam' | 'channels'; direction: 'asc' | 'desc' }>({
    key: 'name',
    direction: 'asc',
  });

  const sortedForms = useMemo(() => {
    return [...(Array.isArray(forms) ? forms : [])].sort((a, b) => {
      const aValue = getSortValue(a, sort.key);
      const bValue = getSortValue(b, sort.key);
      const result = typeof aValue === 'number' && typeof bValue === 'number'
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
      return sort.direction === 'asc' ? result : -result;
    });
  }, [forms, sort]);

  const updateSort = (key: typeof sort.key) => {
    setSort(current => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: key === 'name' || key === 'domain' ? 'asc' : 'desc' });
  };

  return (
    <div className="form-table-container">
      <table className="form-table">
        <thead>
          <tr>
            <SortableTableHeader active={sort.key === 'name'} direction={sort.direction} onClick={() => updateSort('name')}>Name</SortableTableHeader>
            <SortableTableHeader active={sort.key === 'domain'} direction={sort.direction} onClick={() => updateSort('domain')}>Domain</SortableTableHeader>
            <th>Trend</th>
            <SortableTableHeader active={sort.key === 'total'} direction={sort.direction} onClick={() => updateSort('total')}>Status</SortableTableHeader>
            <SortableTableHeader className="form-channels-head" align="right" active={sort.key === 'channels'} direction={sort.direction} onClick={() => updateSort('channels')}>Channels</SortableTableHeader>
            <th>Hash</th>
            {onDeleteForm && <th className="actions-cell"><span className="visually-hidden">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {sortedForms.map((form) => (
            <tr
              key={form.id}
              className={selectedId === form.id ? 'selected' : ''}
              onClick={() => onSelectForm(form)}
            >
              <td className="form-name-cell">
                <div className="form-name-content">
                  <span className="form-name">{form.name}</span>
                  {form.testMode && (
                    <span className="badge badge-test">TEST</span>
                  )}
                </div>
                {form.description && (
                  <span className="form-description">{form.description}</span>
                )}
              </td>
              <td className="form-domain">{form.domain}</td>
              <td className="form-trend-cell">
                {form.stats ? (
                  <Sparkline data={form.stats.trend} width={70} height={18} />
                ) : (
                  <span className="no-data">—</span>
                )}
              </td>
              <td className="form-status-cell">
                {form.stats ? (
                  <div className="status-badges">
                    <span className="status-badge status-approved" title="Approved">
                      {form.stats.approved}
                    </span>
                    <span className="status-badge status-flagged" title="Flagged">
                      {form.stats.flagged}
                    </span>
                    <span className="status-badge status-spam" title="Spam">
                      {form.stats.spam}
                    </span>
                    {(form.stats.shadowbanned ?? 0) > 0 && (
                      <span
                        className="status-badge status-shadowbanned"
                        title="Shadowbanned (security); excluded from totals and trend"
                      >
                        {form.stats.shadowbanned}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="no-data">—</span>
                )}
              </td>
              <td className="form-channels">
                <div className="channel-counts">
                  <span className="channel-count" title="Notification channels">{form.channelCount}</span>
                  {form.flaggedChannelCount !== undefined && form.flaggedChannelCount > 0 && (
                    <span className="channel-count flagged-channel-count" title="Flagged notification channels">
                      {form.flaggedChannelCount}
                      <FlagIcon size={11} />
                    </span>
                  )}
                  {form.spamChannelCount !== undefined && form.spamChannelCount > 0 && (
                    <span className="channel-count spam-channel-count" title="Spam notification channels">
                      {form.spamChannelCount}
                      <BanIcon size={11} />
                    </span>
                  )}
                </div>
              </td>
              <td className="form-hash-cell">
                <code className="form-hash">{form.hash}</code>
              </td>
              {onDeleteForm && (
                <td className="form-actions actions-cell">
                  <button
                    className="icon-btn intent-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteForm(form.id);
                    }}
                    title="Delete form"
                    aria-label="Delete form"
                  >
                    <TrashIcon />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

function getSortValue(form: Form, key: 'name' | 'domain' | 'total' | 'approved' | 'spam' | 'channels') {
  if (key === 'name') return form.name;
  if (key === 'domain') return form.domain;
  if (key === 'channels') return form.channelCount;
  if (key === 'approved') return form.stats?.approved ?? 0;
  if (key === 'spam') return form.stats?.spam ?? 0;
  return form.stats?.total ?? 0;
}

export default FormList;
